import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Facebook, Instagram, Linkedin, Search, Users, BookOpen, BarChart2, Mail, Shield, FileText, Copyright as CopyrightIcon, Lightbulb } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { isFeatureEnabled } from '../../../services/flagsService'
import { openFeatureRequest } from '../../Elements/FeatureRequestLauncher'

const MILLINET_SRC = 'https://millinet.az/banner.js?id=660'

// MilliNet participant badge. Script tags in JSX don't execute, so inject it manually;
// document.write is redirected into the container in case the script relies on it.
const MilliNetBanner: React.FC = () => {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const container = ref.current
    if (!container) return

    const originalWrite = document.write
    document.write = (...html: string[]) => container.insertAdjacentHTML('beforeend', html.join(''))
    const restore = () => { document.write = originalWrite }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = MILLINET_SRC
    script.onload = restore
    script.onerror = restore
    container.appendChild(script)

    return () => {
      restore()
      container.innerHTML = ''
    }
  }, [])

  return <div ref={ref} className="flex justify-center" />
}

const Footer: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const year = new Date().getFullYear()
  const accountsEnabled = isFeatureEnabled('USER_ACCOUNTS')
  const orderEnabled = isFeatureEnabled('PACKAGE_ORDER')
  const featureRequestEnabled = isFeatureEnabled('FEATURE_REQUEST')

  const isImmersiveTimerPage = location.pathname === '/timer'
  if (isImmersiveTimerPage) return null

  const NAV = [
    { to: '/',        label: t('header.mainPage') },
    { to: '/search',  label: t('header.search'),  icon: Search },
    { to: '/authors', label: t('header.authors'), icon: Users },
  ]
  if (isFeatureEnabled('RATING_MENU')) {
    NAV.push({ to: '/ratings', label: 'NHN Reytinqi', icon: BarChart2 })
  }
  if (isFeatureEnabled('LEADERBOARD_MENU')) {
    NAV.push({ to: '/leaderboard',   label: 'Töhfə verənlər',   icon: BarChart2 })
  }

  const SOCIALS = [
    {
      href: 'https://www.facebook.com/3sual.az',
      icon: Facebook,
      label: 'Facebook',
      bg: 'hover:bg-[#1877F2]',
    },
    {
      href: 'https://www.instagram.com/3sual.az',
      icon: Instagram,
      label: 'Instagram',
      bg: 'hover:bg-gradient-to-br hover:from-purple-500 hover:via-pink-500 hover:to-orange-400',
    },
    {
      href: 'https://www.linkedin.com/company/3sual',
      icon: Linkedin,
      label: 'LinkedIn',
      bg: 'hover:bg-[#0A66C2]',
    },
  ]

  return (
    <footer className="bg-slate-100 dark:bg-gray-900 text-slate-600 dark:text-gray-300 border-t border-slate-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mb-10">

          {/* Brand column */}
          <div className="flex flex-col gap-4">
            <Link to="/" className="flex items-center">
              <img
                src="/logo-horizontal.png"
                alt="3sual.az"
                className="h-9 w-auto object-contain dark:hidden"
              />
              <img
                src="/logo-horizontal-dark.png"
                alt=""
                aria-hidden="true"
                className="hidden h-9 w-auto object-contain dark:block"
              />
            </Link>
            <p className="text-sm text-slate-500 dark:text-gray-400 leading-relaxed max-w-xs">
              {t('footer.description')}
            </p>
            {/* Social icons */}
            <div className="flex gap-2 mt-1">
              {SOCIALS.map(({ href, icon: Icon, label, bg }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center",
                    "bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400",
                    "transition-all duration-200 hover:text-white hover:border-transparent hover:scale-110 shadow-sm",
                    bg
                  )}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Nav links */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-blue-500 mb-4 dark:text-indigo-300">
              Navİqasİya
            </h3>
            <ul className="flex flex-col gap-2.5">
              {NAV.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    rel={to === '/leaderboard' ? 'nofollow' : undefined}
                    className="text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-blue-500 mb-4 dark:text-indigo-300">
              Dİgər
            </h3>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {t('footer.contact')}
                </Link>
              </li>
              {featureRequestEnabled && (
                <li>
                  <button
                    type="button"
                    onClick={openFeatureRequest}
                    className="appearance-none text-left text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                  >
                    {/* Inner flex span: Reboot.css's -webkit-appearance: button breaks flex on the button itself */}
                    <span className="flex items-center gap-2">
                      <Lightbulb className="w-3.5 h-3.5" />
                      Funksiya təklif et
                    </span>
                  </button>
                </li>
              )}
              {orderEnabled && (
                <li>
                  <Link
                    to="/order"
                    className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    Paket sifariş et
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/copyright"
                  className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                >
                  <CopyrightIcon className="w-3.5 h-3.5" />
                  {t('legal.copyrightTitle')}
                </Link>
              </li>
              {accountsEnabled && (
                <>
                  <li>
                    <Link
                      to="/terms"
                      className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {t('legal.termsTitle')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-blue-500 transition-colors duration-150 dark:hover:text-indigo-200"
                    >
                      <Shield className="w-3.5 h-3.5" />
                      {t('legal.privacyTitle')}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

        </div>

        <Separator className="bg-slate-200 dark:bg-gray-800 mb-6" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 dark:text-gray-500">
          <p className="text-slate-400 dark:text-gray-500">{t('footer.copyright', { year })}</p>
          <p className="text-slate-300 dark:text-gray-600">3sual.az</p>
        </div>

        {/* MilliNet */}
        <div className="mt-8">
          <MilliNetBanner />
        </div>
      </div>
    </footer>
  )
}

export default Footer
